import { useState, useEffect, createContext, useContext, useCallback, useMemo } from "react";
import axios from "axios";

const API = "/api";
const TOKEN_KEY = "admin_token";
const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const token = sessionStorage.getItem(TOKEN_KEY);
      if (!token) { setLoading(false); return; }
      const { data } = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(data);
    } catch {
      // token expired/invalid — clear and show login
      sessionStorage.removeItem(TOKEN_KEY);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    sessionStorage.setItem(TOKEN_KEY, data.token);
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      const token = sessionStorage.getItem(TOKEN_KEY);
      await axios.post(`${API}/auth/logout`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (err) {
      console.warn("Logout request failed (non-critical):", err?.message);
    }
    sessionStorage.removeItem(TOKEN_KEY);
    setUser(null);
  };

  const hasPermission = useCallback((module, action = "view") => {
    if (!user) return false;
    if (user.role === "admin") return true;
    const perms = user.permissions;
    if (!perms) return false;
    const mod = perms[module];
    if (!mod) return false;
    return action === "view" ? !!mod.view : !!mod.edit;
  }, [user]);

  const isAdmin = user?.role === "admin";

  const contextValue = useMemo(() => ({
    user, loading, login, logout, checkAuth, hasPermission, isAdmin
  }), [user, loading, hasPermission, isAdmin]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

// Axios instance with auth header
// Singleton axios instance — reads token at request time
const apiInstance = axios.create({ baseURL: API });
apiInstance.interceptors.request.use((config) => {
  const token = sessionStorage.getItem(TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Normalize FastAPI 422 errors so `err.response.data.detail` is always a
// string the UI can safely render in a toast. Without this, an array of
// Pydantic error objects bubbles up and crashes React with "Objects are
// not valid as a React child" — exactly what produces the white screen
// when the client form is submitted with an invalid email.
apiInstance.interceptors.response.use(
  (resp) => resp,
  (error) => {
    const data = error?.response?.data;
    if (data && Array.isArray(data.detail)) {
      const messages = data.detail
        .map((d) => {
          if (!d) return "";
          if (typeof d === "string") return d;
          const field = Array.isArray(d.loc) ? d.loc.filter((p) => p !== "body").join(".") : "";
          const msg = d.msg || d.message || "Invalid value";
          return field ? `${field}: ${msg}` : msg;
        })
        .filter(Boolean);
      error.response.data.detail = messages.join("; ") || "Validation error";
    } else if (data && data.detail && typeof data.detail === "object") {
      error.response.data.detail = data.detail.msg || JSON.stringify(data.detail);
    }
    return Promise.reject(error);
  },
);

export function useApi() {
  return apiInstance;
}
